import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "react-query";
import { useManagementTextField } from "hooks/forms/ManagementHooks";
import { useLoadingToast } from "hooks/toast/toasts";
import {
  createTemplateChecklistItem,
  createCompleteTemplate,
  updateTemplateChecklist,
  updateTemplateChecklistItem,
  deleteTemplateChecklistItem
} from "services/api/templateChecklist/templateChecklistApi";
import {
  useFetchTemplateChecklistItemsByChecklistId
} from "hooks/api/templateChecklistApiHooks";

export const useTemplateChecklistEditor = ({
  pdcId,
  template,
  isCreating,
  onSuccess
}) => {
  const queryClient = useQueryClient();
  const { initializeLoadingToast, updateLoadingToast } = useLoadingToast();
  
  // Estado local
  const [items, setItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [nextItemOrder, setNextItemOrder] = useState(1);

  // Campos de formulário - seguindo padrão do projeto
  const { reset: resetTemplateTitle, ...templateTitle } = useManagementTextField(template?.title || "");
  const { reset: resetNewItemContent, ...newItemContent } = useManagementTextField("");

  // Fetch data for editing
  const { templateChecklistItemsLoading, templateChecklistItemsData } = 
    useFetchTemplateChecklistItemsByChecklistId(template?.id);

  const isLoading = templateChecklistItemsLoading && !isCreating;

  // Carregar dados quando estiver editando
  useEffect(() => {
    if (!isCreating && templateChecklistItemsData) {
      const sortedItems = [...templateChecklistItemsData].sort((a, b) => a.order - b.order);
      setItems(sortedItems);
      setNextItemOrder(Math.max(...sortedItems.map(item => item.order), 0) + 1);
    } else if (isCreating) {
      setItems([]);
      setNextItemOrder(1);
    }
  }, [templateChecklistItemsData, isCreating]);

  // Adicionar novo item
  const handleAddItem = useCallback(() => {
    const content = newItemContent.value.trim();
    if (!content) return;

    const newItem = {
      id: Date.now(), // ID temporário para novos itens
      content,
      order: nextItemOrder,
      isChecked: false,
      isNew: true // Flag para identificar itens novos
    };

    setItems(prev => [...prev, newItem]);
    setNextItemOrder(prev => prev + 1);
    newItemContent.onChange({ target: { value: "" } });
  }, [newItemContent, nextItemOrder]);

  // Remover item
  const handleRemoveItem = useCallback((index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Alterar conteúdo do item
  const handleItemContentChange = useCallback((index, newContent) => {
    setItems(prev => prev.map((item, i) => 
      i === index ? { ...item, content: newContent } : item
    ));
  }, []);

  // As funções de onChange estão disponíveis diretamente nos objetos templateTitle e newItemContent

  // Drag and drop
  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return;

    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);

    // Recalcular orders
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    setItems(updatedItems);
  }, [items]);

  // Salvar alterações
  const handleSave = useCallback(async () => {
    if (!templateTitle.value.trim()) {
      return;
    }

    setIsSaving(true);
    let toastId;

    try {
      toastId = initializeLoadingToast({
        text: isCreating ? "Criando template..." : "Salvando alterações..."
      });

      let checklistId = template?.id;

      if (isCreating) {
        // 🚀 NOVA ESTRATÉGIA: Criação completa em uma única requisição
        const completeTemplateData = {
          pdcId,
          title: templateTitle.value.trim(),
          items: items.map(item => ({
            content: item.content.trim(),
            order: item.order
          }))
        };
        
        const response = await createCompleteTemplate(completeTemplateData);
        checklistId = response.data.id;
        
        // Invalidar todas as queries relevantes após criação completa
        queryClient.invalidateQueries(["templateChecklistsByPdc", pdcId]);
        queryClient.invalidateQueries(["allTemplateChecklists"]);
        queryClient.invalidateQueries(["templateChecklistItems", checklistId]);
      } else {
        // Estratégia existente para edição (múltiplas requisições)
        
        // 1. Atualizar o template
        await updateTemplateChecklist({
          id: template.id,
          title: templateTitle.value.trim()
        });

        // 2. Gerenciar itens - primeiro remover itens que não existem mais
        const existingItemIds = templateChecklistItemsData?.map(item => item.id) || [];
        const currentItemIds = items.filter(item => !item.isNew).map(item => item.id);
        const itemsToDelete = existingItemIds.filter(id => !currentItemIds.includes(id));

        for (const itemId of itemsToDelete) {
          await deleteTemplateChecklistItem(itemId);
        }

        // 3. Criar/atualizar itens
        for (const item of items) {
          if (item.isNew) {
            // Criar novo item
            await createTemplateChecklistItem({
              checklistId,
              content: item.content.trim(),
              order: item.order,
              isChecked: false
            });
          } else {
            // Atualizar item existente
            await updateTemplateChecklistItem({
              id: item.id,
              content: item.content.trim(),
              order: item.order,
              isChecked: item.isChecked
            });
          }
        }

        // Invalidar queries para atualizar dados
        queryClient.invalidateQueries(["templateChecklistsByPdc", pdcId]);
        queryClient.invalidateQueries(["templateChecklistItems", checklistId]);
        queryClient.invalidateQueries(["allTemplateChecklists"]);
      }

      updateLoadingToast({
        id: toastId,
        text: isCreating ? "Template criado com sucesso!" : "Alterações salvas com sucesso!",
        type: "success"
      });

      onSuccess?.();
    } catch (error) {
      console.error("Erro ao salvar template:", error);
      updateLoadingToast({
        id: toastId,
        text: error.response?.data?.message || "Erro ao salvar template. Tente novamente.",
        type: "error"
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    templateTitle.value,
    items,
    isCreating,
    template,
    pdcId,
    initializeLoadingToast,
    updateLoadingToast,
    queryClient,
    onSuccess,
    templateChecklistItemsData
  ]);

  // Verificar se pode salvar - permitir criação sem itens conforme nova API
  const canSave = templateTitle.value.trim() && !isSaving;

  return {
    templateTitle,
    items,
    newItemContent,
    isLoading,
    isSaving,
    handleAddItem,
    handleRemoveItem,
    handleItemContentChange,
    handleDragEnd,
    handleSave,
    canSave
  };
};